import { useState, useEffect, useCallback } from 'react';
import { Button, Spin, Tag, Empty, notification, Input, Modal, Switch, Tooltip } from 'antd';
import {
  BankOutlined, CheckCircleOutlined, ClockCircleOutlined, DownloadOutlined, EyeOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl, rozerpay_key_id } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang, ssInvalidate, inrToForeign, fmtDate } from '../shared/utils';
import { ReceiptModal } from '../components/ReceiptModal';

const fmtAmount = (a) => Number(a || 0).toLocaleString('en-IN');

const loadRazorpay = () => new Promise(resolve => {
  if (window.Razorpay) { resolve(true); return; }
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export function PaymentsTab({ quickPayTrigger = 0, joiningDate = null }) {
  const { t } = useLang();
  const [payments,    setPayments]    = useState([]);
  const [accounts,    setAccounts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [total,       setTotal]       = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [shikshaTotal, setShikshaTotal] = useState(0);
  const [page,        setPage]        = useState(1);
  const [bankFilter,  setBankFilter]  = useState(null);
  const [receipt,     setReceipt]     = useState(null);
  const [nriInfo,     setNriInfo]     = useState(null);
  const [wireOpen,    setWireOpen]    = useState(false);
  // Razorpay donate modal
  const [donateOpen,  setDonateOpen]  = useState(false);
  const [months,      setMonths]      = useState(1);
  const [orderInfo,   setOrderInfo]   = useState(null);
  const [paying,      setPaying]      = useState(false);
  const [restartMode, setRestartMode] = useState(false);
  const [visheshMode, setVisheshMode] = useState(false); // विशेष सहयोगी ₹1 लाख
  const [customAmount, setCustomAmount] = useState('');
  const [dueInfo,     setDueInfo]     = useState(null);  // pre-fetched due months
  const [isOneTimePayer, setIsOneTimePayer] = useState(false);
  const [freshStart,     setFreshStart]     = useState(false);
  // Auto-pay subscription
  const [subStatus,      setSubStatus]      = useState(null);  // null | 'created' | 'authenticated' | 'active' | 'cancelled' | 'halted'
  const [subId,          setSubId]          = useState(null);
  const [subNextCharge,  setSubNextCharge]  = useState(null);  // Unix timestamp
  const [subLoading,     setSubLoading]     = useState(false);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [newPlanAmount,  setNewPlanAmount]  = useState(null);
  const [allPayments,    setAllPayments]    = useState([]); // all payments for the monthly grid (unpaginated)
  const [pendingStartOverride, setPendingStartOverride] = useState(null); // YYYY-MM when user clicks a specific unpaid month
  const PAGE_SIZE = 10;

  // Load subscription status once
  useEffect(() => {
    axios.get(`${baseUrl}/member/razorpay/subscription-status`, { headers: authHeader() })
      .then(r => {
        if (r.data.status) {
          setSubStatus(r.data.subscription_status);
          setSubId(r.data.subscription_id);
          setSubNextCharge(r.data.next_charge_at || null);
        }
      })
      .catch(() => {});
  }, []);

  const enableAutoPay = () => {
    Modal.confirm({
      title: '⚡ Auto-pay चालू करें?',
      content: (
        <div style={{ marginTop: 8 }}>
          <p>हर माह <strong>₹{fmtAmount(dueInfo?.share)}</strong> अपने आप कटेगा।</p>
          <p style={{ color: '#6b7280', fontSize: 13 }}>
            UPI Autopay से mandate approve करें (GPay, PhonePe, BHIM आदि)।<br />
            कभी भी बंद कर सकते हैं।
          </p>
        </div>
      ),
      okText: 'चालू करें',
      cancelText: 'रद्द करें',
      okButtonProps: { style: { backgroundColor: '#7a0000', borderColor: '#7a0000' } },
      onOk: _doEnableAutoPay,
    });
  };

  const _doEnableAutoPay = async () => {
    setSubLoading(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { notification.error({ message: 'Razorpay SDK load failed.' }); return; }

      const res = await axios.post(`${baseUrl}/member/razorpay/create-subscription`, {}, { headers: authHeader() });
      if (!res.data.subscription_id) throw new Error(res.data.message || 'Subscription creation failed');

      setSubId(res.data.subscription_id);
      setSubStatus('created');

      const rzp = new window.Razorpay({
        key:             rozerpay_key_id,
        subscription_id: res.data.subscription_id,
        name:            'समाज शिक्षा निधि',
        description:     `Auto-pay ₹${(res.data.monthly_share || '').toLocaleString('en-IN')}/माह — समर्पण योजना`,
        prefill:         { name: res.data.name, contact: res.data.phone, ...(res.data.email ? { email: res.data.email } : {}) },
        readonly:        { contact: !!res.data.phone, ...(res.data.email ? { email: true } : {}) },
        theme:           { color: '#7a0000' },
        handler: () => {
          setSubStatus('authenticated');
          notification.success({
            message: '✅ Auto-pay चालू हो गया!',
            description: 'अब हर माह आपकी राशि automatically कट जाएगी।',
            duration: 6, placement: 'topRight',
          });
        },
        modal: { ondismiss: () => setSubLoading(false) },
      });
      rzp.open();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || err.message || 'Auto-pay setup failed', placement: 'topRight' });
      setSubLoading(false);
    }
  };

  const cancelAutoPay = () => {
    Modal.confirm({
      title: 'Auto-pay बंद करें?',
      content: (
        <div style={{ marginTop: 8 }}>
          <p>Auto-pay बंद करने के बाद अगले माह से automatic deduction नहीं होगा।</p>
          <p style={{ color: '#6b7280', fontSize: 13 }}>आप बाद में दोबारा चालू कर सकते हैं।</p>
        </div>
      ),
      okText: 'बंद करें',
      cancelText: 'रहने दें',
      okType: 'danger',
      onOk: async () => {
        setSubLoading(true);
        try {
          await axios.delete(`${baseUrl}/member/razorpay/cancel-subscription`, { headers: authHeader() });
          setSubStatus('cancelled');
          setSubId(null);
          setSubNextCharge(null);
          notification.success({ message: '✅ Auto-pay बंद हो गया', description: 'आप जब चाहें दोबारा चालू कर सकते हैं।', placement: 'topRight' });
        } catch (err) {
          notification.error({ message: err.response?.data?.message || 'Cancel failed', placement: 'topRight' });
        } finally { setSubLoading(false); }
      },
    });
  };

  const PLAN_AMOUNTS = [200, 500, 1100, 2100, 5100, 11000];

  const confirmChangePlan = async () => {
    if (!newPlanAmount) return;
    setSubLoading(true);
    setChangePlanOpen(false);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { notification.error({ message: 'Razorpay SDK load failed.' }); return; }

      const res = await axios.patch(`${baseUrl}/member/razorpay/change-plan`,
        { new_amount: newPlanAmount }, { headers: authHeader() });
      if (!res.data.subscription_id) throw new Error(res.data.message || 'Plan change failed');

      setSubId(res.data.subscription_id);
      setSubStatus('created');
      setSubNextCharge(null);

      const rzp = new window.Razorpay({
        key:             rozerpay_key_id,
        subscription_id: res.data.subscription_id,
        name:            'समाज शिक्षा निधि',
        description:     `Auto-pay ₹${newPlanAmount.toLocaleString('en-IN')}/माह — समर्पण योजना`,
        prefill:         { name: res.data.name, contact: res.data.phone },
        theme:           { color: '#7a0000' },
        handler: () => {
          setSubStatus('authenticated');
          notification.success({
            message: `✅ Plan बदल गया — ₹${newPlanAmount.toLocaleString('en-IN')}/माह`,
            description: 'नया UPI Autopay mandate set हो गया।',
            duration: 6, placement: 'topRight',
          });
        },
        modal: { ondismiss: () => setSubLoading(false) },
      });
      rzp.open();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || err.message || 'Plan change failed', placement: 'topRight' });
      setSubLoading(false);
    }
  };

  // Load bank accounts + pre-fetch due info + NRI wire info once
  useEffect(() => {
    axios.get(`${baseUrl}/member/nri-payment-info`, { headers: authHeader() })
      .then(r => { if (r.data.status) setNriInfo(r.data.result); })
      .catch(() => {});
    axios.get(`${baseUrl}/member/bank-accounts`, { headers: authHeader() })
      .then(r => { if (r.data.status) setAccounts(r.data.result || []); })
      .catch(() => {});
    axios.get(`${baseUrl}/member/razorpay/payment-info`, { headers: authHeader() })
      .then(r => {
        if (!r.data.status) return;
        if (r.data.payment_commitment === 'one_time') { setIsOneTimePayer(true); return; }
        setIsOneTimePayer(false);
        const lastEnd = r.data.last_end_month ? new Date(r.data.last_end_month) : null;
        const now     = new Date();
        let due = 0;
        if (lastEnd) {
          due = Math.max(0, (now.getFullYear() - lastEnd.getFullYear()) * 12 + (now.getMonth() - lastEnd.getMonth()) - 1);
        }
        setDueInfo({ due, share: Number(r.data.monthly_share) || 0, lastEnd });
      })
      .catch(() => {});
  }, []);

  const fetchPayments = useCallback(async (pg = 1, bankId = bankFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE });
      if (bankId) params.set('bank_account_id', bankId);
      const res = await axios.get(`${baseUrl}/member/payments?${params}`, { headers: authHeader() });
      if (res.data.status) {
        setPayments(res.data.result || []);
        setTotal(res.data.total || 0);
        setTotalAmount(res.data.total_amount || 0);
        if (res.data.shiksha_total !== undefined) setShikshaTotal(res.data.shiksha_total);
        setPage(pg);
      }
    } catch { notification.error({ message: 'Payments load failed', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [bankFilter]);

  useEffect(() => { fetchPayments(1, bankFilter); }, [bankFilter]); // eslint-disable-line

  // Fetch ALL payments (no pagination) for the monthly status grid
  useEffect(() => {
    axios.get(`${baseUrl}/member/payments?page=1&limit=500`, { headers: authHeader() })
      .then(r => { if (r.data.status) setAllPayments(r.data.result || []); })
      .catch(() => {});
  }, []); // eslint-disable-line

  const selectBank = (id) => { setBankFilter(id); };
  const pagePaid   = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── Razorpay flow ──────────────────────────────────────────────────────────
  const openDonateModal = async (preDue = null, startOverride = null) => {
    setMonths(preDue ?? 1);
    setPendingStartOverride(startOverride);
    setOrderInfo(null);
    setRestartMode(false);
    setVisheshMode(false);
    setDonateOpen(true);
    try {
      const res = await axios.get(`${baseUrl}/member/razorpay/payment-info`, { headers: authHeader() });
      setOrderInfo(res.data.status ? res.data : { error: res.data.message });
    } catch (err) {
      setOrderInfo({ error: err.response?.data?.message || 'विवरण लोड नहीं हो सका' });
    }
  };

  // Auto-open donate modal when triggered from the home quick-pay button
  useEffect(() => { if (quickPayTrigger > 0) openDonateModal(); }, [quickPayTrigger]); // eslint-disable-line

  const startRazorpayPayment = async () => {
    setPaying(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { notification.error({ message: 'Razorpay SDK load failed. Check internet.' }); return; }

      // Create order
      const parsedCustom = customAmount ? parseFloat(customAmount) : 0;
      const orderRes = await axios.post(`${baseUrl}/member/razorpay/create-order`,
        {
          months:               visheshMode ? 'vishesh' : months,
          source:               visheshMode ? 'Vishesh-Sahyogi' : 'Member-Portal',
          custom_amount:        (!visheshMode && parsedCustom > 0) ? parsedCustom : undefined,
          start_month_override: (!visheshMode && pendingStartOverride) ? pendingStartOverride : undefined,
        },
        { headers: authHeader() });
      if (!orderRes.data.order_id) throw new Error(orderRes.data.message || 'Order creation failed');

      const { order_id, amount, name, phone, bank_account_id } = orderRes.data;

      const rzp = new window.Razorpay({
        key:         rozerpay_key_id,
        order_id,
        amount:      amount * 100,
        currency:    'INR',
        name:        'समाज शिक्षा निधि',
        description: visheshMode ? 'विशेष समर्पण सहयोग — ₹1,00,000' : `समाज शिक्षा निधि समर्पण — ${months} माह`,
        prefill:     { name, contact: phone },
        theme:       { color: '#7a0000' },
        // UPI apps: redirect back to portal after payment instead of staying in UPI app
        redirect:    true,
        callback_url: `${window.location.origin}/user-portal?payment_return=1`,
        handler: async (response) => {
          try {
            const vRes = await axios.post(`${baseUrl}/member/razorpay/verify-payment`, {
              order_id:             response.razorpay_order_id,
              payment_id:           response.razorpay_payment_id,
              signature:            response.razorpay_signature,
              months:               visheshMode ? 'vishesh' : months,
              bank_account_id,
              restart_mode:         restartMode,
              source:               visheshMode ? 'Vishesh-Sahyogi' : 'Member-Portal',
              custom_amount:        (!visheshMode && parsedCustom > 0) ? parsedCustom : undefined,
              start_month_override: (!visheshMode && pendingStartOverride) ? pendingStartOverride : undefined,
            }, { headers: authHeader() });

            if (vRes.data.status) {
              notification.success({
                message: '✅ भुगतान सफल!',
                description: 'आपका समर्पण दर्ज हो गया। Verification कुछ मिनटों में होगी।',
                duration: 6, placement: 'topRight',
              });
              setDonateOpen(false);
              ssInvalidate('home_dash_v2');  // home must re-fetch paid status
              fetchPayments(1, bankFilter);  // refresh list
            }
          } catch (err) {
            notification.error({ message: err.response?.data?.message || 'Payment recording failed', placement: 'topRight' });
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || err.message || 'Payment failed', placement: 'topRight' });
      setPaying(false);
    }
  };

  const downloadOrSharePoster = async (paymentId, amount, date) => {
    const token = localStorage.getItem('memberToken');
    try {
      const r = await fetch(`${baseUrl}/member/poster/${paymentId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('not available');
      const blob = await r.blob();
      const file = new File([blob], `contribution_poster_${paymentId}.jpg`, { type: 'image/jpeg' });

      // Try native share (WhatsApp status, Instagram, etc.) on mobile
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'समाज शिक्षा निधि समर्पण',
          text: `मैंने समाज शिक्षा निधि समर्पण योजना में ₹${fmtAmount(amount)} का समर्पण किया। दिनांक: ${fmtDate(date)}`,
        });
      } else {
        // Desktop fallback — download the file
        const url = URL.createObjectURL(blob);
        const a   = document.createElement('a');
        a.href     = url;
        a.download = `contribution_poster_${paymentId}.jpg`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        notification.warning({ message: 'Poster not available for this payment yet.', placement: 'topRight' });
      }
    }
  };


const [otp_switchSent, setOtpSwitchSent] = useState(false);
const [otp_switching, setOtpSwitching] = useState(false);
const [otp_amount, setOtpAmount] = useState('');

  return (
    <div>
      {/* Summary + Pay Now merged */}
      <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, borderRadius: 14, padding: '18px 20px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
            कुल योगदान राशि
          </div>
          <div style={{ fontSize: 30, fontWeight: 900, color: G, letterSpacing: -0.5 }}>
            ₹ {fmtAmount(shikshaTotal)}
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 6, padding: '2px 8px', color: '#fde68a', fontWeight: 700 }}>
              💛 {accounts.find(a => a.id === 1)?.account_name || 'समाज शिक्षा निधि समर्पण'}
            </span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>Online payment · तुरंत verified receipt</span>
          </div>
          {bankFilter && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', display: 'inline' }}>{total}</div>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginLeft: 6 }}>इस खाते में लेन-देन</span>
            </div>
          )}
        </div>
        <button onClick={() => openDonateModal()} style={{
          background: '#FDE68A', color: '#7a0000', border: 'none',
          borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 800,
          cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        }}>
          <BankOutlined /> Pay Now
        </button>
      </div>

      {/* ── DUE MONTHS BANNER (shown when member has unpaid months, not for one-time payers) ── */}
      {!isOneTimePayer && dueInfo && dueInfo.due > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #7f1d1d, #991b1b)',
          borderRadius: 12, padding: '14px 16px', marginBottom: 10,
          border: '2px solid #fca5a5',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#FDE68A', display: 'flex', alignItems: 'center', gap: 6 }}>
              ⚠️ बकाया समर्पण
              <span style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 900,
                borderRadius: 20, padding: '1px 7px', marginLeft: 4 }}>
                {dueInfo.due} माह
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 3 }}>
              कुल बकाया: ₹{(dueInfo.share * dueInfo.due).toLocaleString('en-IN')} · तुरंत भुगतान करें
            </div>
          </div>
          <button onClick={() => openDonateModal(Math.min(dueInfo.due, 12))} style={{
            background: '#ef4444', color: '#fff', border: 'none',
            borderRadius: 9, padding: '8px 16px', fontSize: 12, fontWeight: 800,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            बकाया भरें →
          </button>
        </div>
      )}

{/* ── AUTO-PAY SUBSCRIPTION CARD (only for monthly payers) ── */}
      {!isOneTimePayer && (() => {
        const isActive  = subStatus === 'active' || subStatus === 'authenticated';
        const isPending = subStatus === 'created';
        const isHalted  = subStatus === 'halted';
        const nextChargeDate = subNextCharge
          ? new Date(subNextCharge * 1000).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })
          : null;
        return (
          <div style={{
            borderRadius: 12, marginBottom: 14, overflow: 'hidden',
            border: `1.5px solid ${isActive ? '#86efac' : isHalted ? '#fca5a5' : isPending ? '#fcd34d' : '#e5e7eb'}`,
            background: isActive ? 'linear-gradient(135deg,#f0fdf4,#dcfce7)' : isHalted ? '#fef2f2' : isPending ? '#fffbeb' : '#f9fafb',
          }}>
            <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: isActive ? '#15803d' : isHalted ? '#dc2626' : '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                  🔄 Auto-pay (UPI Autopay)
                  {isActive  && <span style={{ background: '#15803d', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: 20, padding: '1px 8px' }}>ACTIVE</span>}
                  {isPending && <span style={{ background: '#f59e0b', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: 20, padding: '1px 8px' }}>Mandate Pending</span>}
                  {isHalted  && <span style={{ background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: 20, padding: '1px 8px' }}>HALTED</span>}
                </div>
                <div style={{ fontSize: 11, color: isHalted ? '#dc2626' : '#6b7280', marginTop: 2 }}>
                  {isActive
                    ? 'हर माह अपने आप राशि कटेगी — कुछ याद नहीं रखना।'
                    : isPending
                      ? 'Mandate setup pending — UPI app से approve करें।'
                      : isHalted
                        ? '⚠️ Auto-pay रुक गया — account में balance check करें और नीचे से manually pay करें।'
                        : 'Enable करें — हर माह automatic समर्पण होगा।'}
                </div>
                {isActive && nextChargeDate && (
                  <div style={{ marginTop: 5, fontSize: 11, color: '#15803d', fontWeight: 700,
                    background: '#dcfce7', borderRadius: 6, padding: '3px 8px', display: 'inline-block' }}>
                    📅 अगला भुगतान: {nextChargeDate}
                  </div>
                )}
              </div>
              {isActive ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                  <button
                    disabled={subLoading}
                    onClick={() => { setNewPlanAmount(null); setChangePlanOpen(true); }}
                    style={{
                      background: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd',
                      borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>
                    ✏️ Plan बदलें
                  </button>
                  <button
                    disabled={subLoading}
                    onClick={cancelAutoPay}
                    style={{
                      background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
                      borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>
                    {subLoading ? '...' : 'बंद करें'}
                  </button>
                </div>
              ) : (
                <button
                  disabled={subLoading || isPending}
                  onClick={enableAutoPay}
                  style={{
                    background: isHalted ? '#dc2626' : isPending ? '#f59e0b' : '#15803d',
                    color: '#fff', border: 'none',
                    borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 800,
                    cursor: isPending ? 'default' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    opacity: subLoading ? 0.6 : 1,
                  }}>
                  {subLoading ? 'लोड हो रहा है…' : isPending ? '⏳ Approve करें' : isHalted ? '🔁 फिर से setup करें' : '⚡ Enable करें'}
                </button>
              )}
            </div>
          </div>
        );
      })()}

{/* ── ONE-TIME PAYER: custom amount pay + switch to monthly ── */}
{isOneTimePayer && (
  <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #bfdbfe', marginBottom: 16, overflow: 'hidden' }}>

    {/* Header */}
    <div style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>एकमुश्त भुगतान</div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>
          कोई भी राशि — एक बार में समर्पण करें
        </div>
      </div>
    </div>

    <div style={{ padding: '10px 14px' }}>

      {/* Custom amount input */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          type="number"
          min={1}
          placeholder="राशि दर्ज करें (जैसे 500, 1000)"
          value={otp_amount}
          onChange={e => setOtpAmount(e.target.value)}
          style={{ flex: 1, borderRadius: 8, border: '1.5px solid #d1d5db', padding: '6px 10px', fontSize: 13, outline: 'none' }}
        />
        <button
          disabled={!otp_amount || Number(otp_amount) < 1}
          onClick={() => {
            setCustomAmount(otp_amount);
            openDonateModal();
          }}
          style={{
            background: otp_amount && Number(otp_amount) >= 1 ? '#1d4ed8' : '#e5e7eb',
            color: otp_amount && Number(otp_amount) >= 1 ? '#fff' : '#9ca3af',
            border: 'none', borderRadius: 8, padding: '6px 14px',
            fontSize: 12, fontWeight: 700, cursor: otp_amount && Number(otp_amount) >= 1 ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
          }}
        >
          <BankOutlined /> Pay
        </button>
      </div>

      {/* Switch to monthly */}
      <div>
        {!otp_switchSent ? (
          <button
            disabled={otp_switching}
            onClick={async () => {
              setOtpSwitching(true);
              try {
                await axios.post(`${baseUrl}/member/inquiry`, {
                  inquiry_type: 'Payment Issue',
                  subject: 'मासिक भुगतान प्रकार हेतु अनुरोध',
                  message: 'मैं अपनी सदस्यता को एकमुश्त से मासिक (Monthly) में परिवर्तित करना चाहता/चाहती हूं। कृपया अपडेट करें।',
                }, { headers: authHeader() });
                setOtpSwitchSent(true);
              } catch {
                /* silent */
              } finally {
                setOtpSwitching(false);
              }
            }}
            style={{
              background: 'none', border: '1px solid #d1d5db', borderRadius: 7,
              padding: '4px 10px', fontSize: 11, color: '#374151', cursor: 'pointer',
            }}
          >
            {otp_switching ? 'भेज रहे हैं…' : 'मासिक में बदलें →'}
          </button>
        ) : (
          <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>✓ अनुरोध भेजा — Admin अपडेट करेंगे</span>
        )}
      </div>

    </div>
  </div>
)}


      {/* ── NRI / INTERNATIONAL PAYMENT CARD ── */}
      <div style={{
        borderRadius: 14, marginBottom: 16, overflow: 'hidden',
        border: '1.5px solid #fbbf24', boxShadow: '0 2px 12px rgba(251,191,36,0.18)',
      }}>
        {/* Header */}
        <div
          onClick={() => setWireOpen(o => !o)}
          style={{
            background: 'linear-gradient(135deg, #78350f, #b45309)',
            padding: '13px 16px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 900, color: '#fef3c7' }}>
              🌍 {t('अंतर्राष्ट्रीय / NRI समर्पण', 'International / NRI Contribution')}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(254,243,199,0.75)', marginTop: 2 }}>
              {t('विदेश से Bank Wire Transfer या International Card द्वारा',
                 'Bank Wire Transfer or International Card from abroad')}
            </div>
          </div>
          <span style={{ fontSize: 18, color: '#fef3c7' }}>{wireOpen ? '▲' : '▼'}</span>
        </div>

        {wireOpen && (
          <div style={{ background: '#fffbeb', padding: '14px 16px' }}>

            {/* Razorpay International note */}
            <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 10, padding: '10px 13px', marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e', marginBottom: 4 }}>
                💳 {t('International Card से Pay करें', 'Pay with International Card')}
              </div>
              <div style={{ fontSize: 11, color: '#78350f', lineHeight: 1.6 }}>
                {t(
                  'ऊपर "Pay Now" बटन पर click करें — Razorpay International Cards (Visa / Mastercard / Amex) accept करता है। आपकी currency में automatically convert होगा।',
                  'Click "Pay Now" above — Razorpay accepts International Visa / Mastercard / Amex cards and auto-converts your currency to INR.'
                )}
              </div>
              <button onClick={() => setDonateOpen(true)} style={{
                marginTop: 8, background: '#92400e', color: '#fef3c7', border: 'none',
                borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
              }}>
                💳 {t('Card से Pay करें', 'Pay with Card')}
              </button>
            </div>

            {/* Bank Wire section */}
            <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e', marginBottom: 8 }}>
              🏦 {t('Bank Wire Transfer (SWIFT)', 'Bank Wire Transfer (SWIFT)')}
            </div>

            {nriInfo && (nriInfo.bank_name || nriInfo.account_no) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[
                  { label: t('खाताधारक', 'Account Holder'), value: nriInfo.account_holder },
                  { label: t('बैंक', 'Bank'),               value: nriInfo.bank_name },
                  { label: t('खाता नंबर', 'Account No.'),   value: nriInfo.account_no },
                  { label: 'IFSC',                           value: nriInfo.ifsc },
                  { label: 'SWIFT / BIC',                   value: nriInfo.swift },
                  { label: 'UPI / GPay',                    value: nriInfo.upi || nriInfo.gpay },
                ].filter(r => r.value).map(row => (
                  <div key={row.label} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#fff', borderRadius: 8, padding: '8px 11px',
                    border: '1px solid #fde68a',
                  }}>
                    <div>
                      <div style={{ fontSize: 9, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{row.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a1a', fontFamily: 'monospace' }}>{row.value}</div>
                    </div>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(row.value); notification.success({ message: `${row.label} copied!`, placement: 'topRight', duration: 1.5 }); }}
                      style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, color: '#92400e', cursor: 'pointer' }}>
                      {t('कॉपी', 'Copy')}
                    </button>
                  </div>
                ))}
                {nriInfo.email && (
                  <div style={{ fontSize: 11, color: '#b45309', marginTop: 4, textAlign: 'center' }}>
                    {t('Transfer के बाद screenshot भेजें:', 'After transfer send screenshot to:')} <strong>{nriInfo.email}</strong>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '16px 0', color: '#b45309', fontSize: 12 }}>
                {t(
                  'Wire transfer details के लिए admin से संपर्क करें।',
                  'Contact admin for wire transfer details.'
                )}
              </div>
            )}

            {/* Currency estimate info */}
            <div style={{ marginTop: 12, background: '#fff7ed', borderRadius: 8, padding: '9px 12px', border: '1px solid #fed7aa' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9a3412', marginBottom: 4 }}>
                💱 {t('अनुमानित रूपांतरण (Approximate)', 'Approximate Currency Conversion')}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[200, 500, 1000, 2400].map(inr => {
                  const usd = inrToForeign(inr, 'US');
                  const gbp = inrToForeign(inr, 'GB');
                  const aed = inrToForeign(inr, 'AE');
                  return (
                    <div key={inr} style={{ background: '#fff', borderRadius: 6, padding: '5px 9px', border: '1px solid #fed7aa', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, fontWeight: 900, color: '#9a3412' }}>₹{inr}</div>
                      <div style={{ fontSize: 9, color: '#78350f' }}>{usd} · {gbp} · {aed}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 9, color: '#b45309', marginTop: 5 }}>
                * {t('दरें अनुमानित हैं। वास्तविक रूपांतरण आपके बैंक पर निर्भर करेगा।',
                     'Rates are approximate. Actual conversion depends on your bank.')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── RAZORPAY PAYMENT MODAL ── */}
      <Modal
        open={donateOpen}
        onCancel={() => { setDonateOpen(false); setPaying(false); setCustomAmount(''); setPendingStartOverride(null); }}
        footer={null} centered width={440}
        styles={{ body: { padding: 0 } }}
        title={null}
        closable={false}
      >
        {!orderInfo ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
        ) : orderInfo.error ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10,
              padding: 16, fontSize: 13, color: '#b91c1c' }}>
              {orderInfo.error}
            </div>
            <button onClick={() => setDonateOpen(false)} style={{ marginTop: 12, background: 'none',
              border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 12 }}>
              बंद करें
            </button>
          </div>
        ) : (() => {
          const share   = Number(orderInfo.monthly_share) || 0;
          const lastEnd = orderInfo.last_end_month ? new Date(orderInfo.last_end_month) : null;
          const now     = new Date();

          // Calculate due months (past unpaid)
          let dueMonths = 0;
          if (lastEnd) {
            const lastYear  = lastEnd.getFullYear();
            const lastMonth = lastEnd.getMonth();
            const nowYear   = now.getFullYear();
            const nowMonth  = now.getMonth();
            dueMonths = Math.max(0, (nowYear - lastYear) * 12 + (nowMonth - lastMonth) - 1);
          }

          const isLongInactive = dueMonths > 12;  // more than 1 year gap

          // Coverage period based on mode
          const baseStart = pendingStartOverride
            ? (() => { const [yr, mo] = pendingStartOverride.split('-').map(Number); return new Date(yr, mo - 1, 1); })()
            : restartMode
              ? new Date(now.getFullYear(), now.getMonth(), 1)
              : (lastEnd
                  ? new Date(lastEnd.getFullYear(), lastEnd.getMonth() + 1, 1)
                  : new Date(now.getFullYear(), now.getMonth(), 1));

          const visheshMonths = 12;
          const startFrom = baseStart;
          const endAt     = visheshMode
            ? new Date(startFrom.getFullYear(), startFrom.getMonth() + visheshMonths, 0)
            : new Date(startFrom.getFullYear(), startFrom.getMonth() + months, 0);

          const fmtMon  = (d) => d.toLocaleDateString('hi-IN', { month: 'long', year: 'numeric' });
          const parsedCustom = customAmount ? parseFloat(customAmount) : 0;
          const totalAmt = visheshMode ? 100000 : (parsedCustom > 0 ? parsedCustom : share * months);

          // Month options
          const monthOptions = dueMonths > 0 && !restartMode
            ? [dueMonths, 1, 3, 6, 12].filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b)
            : [1, 3, 6, 12];

          return (
            <div>
              {/* Header */}
              <div style={{
                background: `linear-gradient(135deg, ${M} 0%, #b71c1c 100%)`,
                padding: '20px 24px 18px', borderRadius: '8px 8px 0 0',
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              }}>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>
                    समाज शिक्षा निधि समर्पण योजना
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#FDE68A' }}>
                    समर्पण भुगतान
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
                    🔒 Razorpay द्वारा सुरक्षित
                  </div>
                </div>
                <button onClick={() => setDonateOpen(false)} style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                  width: 30, height: 30, cursor: 'pointer', color: '#fff', fontSize: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>✕</button>
              </div>

              <div style={{ padding: '20px 24px 24px' }}>

                {/* Specific pending month selected from grid */}
                {pendingStartOverride && !visheshMode && (
                  <div style={{
                    background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
                    border: '1.5px solid #93c5fd', borderRadius: 12,
                    padding: '12px 16px', marginBottom: 16,
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ fontSize: 20 }}>📌</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8' }}>
                        बकाया माह का समर्पण
                      </div>
                      <div style={{ fontSize: 11, color: '#1e40af', marginTop: 2 }}>
                        आपने {new Date(parseInt(pendingStartOverride.split('-')[0]), parseInt(pendingStartOverride.split('-')[1]) - 1, 1)
                          .toLocaleDateString('hi-IN', { month: 'long', year: 'numeric' })} से भुगतान चुना है।
                        {' '}नीचे से माह संख्या बदल सकते हैं।{' '}
                        <button onClick={() => setPendingStartOverride(null)}
                          style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 11 }}>
                          रद्द करें
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Inactive / due months — soft welcoming message */}
                {dueMonths > 0 && !pendingStartOverride && (
                  <div style={{
                    background: isLongInactive ? 'linear-gradient(135deg,#fffbeb,#fef3c7)' : '#F0FDF4',
                    border: `1.5px solid ${isLongInactive ? '#FCD34D' : '#86EFAC'}`,
                    borderRadius: 12, padding: '14px 16px', marginBottom: 16,
                  }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                      <span style={{ fontSize: 22, flexShrink: 0 }}>{isLongInactive ? '🙏' : '💛'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: isLongInactive ? '#92400E' : '#15803D', marginBottom: 3 }}>
                          {isLongInactive
                            ? 'आपका स्वागत है — फिर से जुड़ें!'
                            : `${dueMonths} माह का समर्पण शेष है`}
                        </div>
                        <div style={{ fontSize: 11, color: isLongInactive ? '#B45309' : '#166534', lineHeight: 1.5 }}>
                          {isLongInactive
                            ? `आप ${dueMonths} माह से समाज से दूर थे। कोई बात नहीं — आज से फिर समर्पण शुरू करें। आप चाहें तो शेष माह एक साथ समर्पित करें, या आज से नया आरंभ करें।`
                            : `${fmtMon(lastEnd)} के बाद ${dueMonths} माह का समर्पण शेष है।`}
                        </div>
                      </div>
                    </div>

                    {/* Two options for long-inactive members */}
                    {isLongInactive && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => { setRestartMode(false); setMonths(Math.min(dueMonths, 12)); }}
                          style={{
                            flex: 1, padding: '8px 6px', borderRadius: 9, fontSize: 11, fontWeight: 700,
                            cursor: 'pointer', transition: 'all 0.15s',
                            background: !restartMode ? '#92400E' : '#fff',
                            color:      !restartMode ? '#FDE68A' : '#92400E',
                            border:     `1.5px solid #92400E`,
                          }}>
                          🙏 शेष माह समर्पण करें<br />
                          <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.85 }}>
                            ₹{(share * Math.min(dueMonths, 12)).toLocaleString('en-IN')} से आरंभ
                          </span>
                        </button>
                        <button
                          onClick={() => { setRestartMode(true); setMonths(1); }}
                          style={{
                            flex: 1, padding: '8px 6px', borderRadius: 9, fontSize: 11, fontWeight: 700,
                            cursor: 'pointer', transition: 'all 0.15s',
                            background: restartMode ? '#15803D' : '#fff',
                            color:      restartMode ? '#fff' : '#15803D',
                            border:     `1.5px solid #15803D`,
                          }}>
                          🌱 आज से नया शुरू<br />
                          <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.85 }}>
                            पुराना छोड़कर आगे बढ़ें
                          </span>
                        </button>
                      </div>
                    )}

                    {/* Short due — just show select-all button */}
                    {!isLongInactive && (
                      <button onClick={() => setMonths(dueMonths)} style={{
                        background: '#15803D', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '5px 14px', fontSize: 11, fontWeight: 700,
                        cursor: 'pointer',
                      }}>
                        सभी {dueMonths} माह समर्पित करें →
                      </button>
                    )}
                  </div>
                )}

                {/* Monthly share + last paid — hidden for one-time payers */}
                {!isOneTimePayer && (
                <div style={{
                  background: '#F8FAFC', borderRadius: 10, padding: '14px 16px',
                  marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      मासिक समर्पण राशि
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 900, color: M, lineHeight: 1.1, marginTop: 3 }}>
                      ₹ {share.toLocaleString('en-IN')}
                    </div>
                  </div>
                  {lastEnd && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase' }}>पिछला भुगतान तक</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginTop: 3 }}>{fmtMon(lastEnd)}</div>
                      {dueMonths === 0 && (
                        <div style={{ fontSize: 10, color: '#15803D', marginTop: 2, fontWeight: 600 }}>✓ Up to date</div>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Months selector — hidden for one-time payers */}
                {!isOneTimePayer && <div style={{ marginBottom: 12, opacity: visheshMode ? 0.4 : 1, pointerEvents: visheshMode ? 'none' : 'auto' }}>

                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    माह चुनें
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {monthOptions.map(m => (
                      <button key={m} onClick={() => setMonths(m)} style={{
                        flex: '1 1 auto', padding: '10px 8px', borderRadius: 10,
                        fontWeight: 800, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                        background:  months === m ? M : '#F9FAFB',
                        color:       months === m ? '#FDE68A' : '#374151',
                        border:      months === m ? `2px solid ${M}` : '1.5px solid #E5E7EB',
                        position: 'relative',
                      }}>
                        {m === dueMonths && dueMonths > 0 && (
                          <span style={{ position: 'absolute', top: -6, right: -6, background: '#EF4444',
                            color: '#fff', fontSize: 8, fontWeight: 800, borderRadius: 20,
                            padding: '1px 5px', lineHeight: 1.4 }}>शेष</span>
                        )}
                        {m} माह
                        <div style={{ fontSize: 9, fontWeight: 600, marginTop: 2,
                          color: months === m ? 'rgba(253,230,138,0.8)' : '#9CA3AF' }}>
                          ₹{(share * m).toLocaleString('en-IN')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>}

                {/* Custom Amount Input */}
                {!visheshMode && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      अपनी राशि दर्ज करें <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional — माह अनुसार राशि से अलग हो तो)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center',
                        border: `1.5px solid ${customAmount ? M : '#E5E7EB'}`,
                        borderRadius: 10, overflow: 'hidden', flex: 1,
                        background: customAmount ? '#fff8f0' : '#fff',
                      }}>
                        <span style={{ padding: '0 10px', fontSize: 16, fontWeight: 700, color: M }}>₹</span>
                        <input
                          type="number"
                          min="1"
                          placeholder={`${(share * months).toLocaleString('en-IN')} (default)`}
                          value={customAmount}
                          onChange={e => setCustomAmount(e.target.value)}
                          style={{
                            flex: 1, border: 'none', outline: 'none',
                            padding: '10px 8px 10px 0', fontSize: 15, fontWeight: 700,
                            color: '#374151', background: 'transparent',
                          }}
                        />
                      </div>
                      {customAmount && (
                        <button onClick={() => setCustomAmount('')} style={{
                          background: '#fee2e2', border: 'none', borderRadius: 8,
                          padding: '10px 12px', cursor: 'pointer', fontSize: 12,
                          color: '#b91c1c', fontWeight: 700,
                        }}>✕</button>
                      )}
                    </div>
                    {customAmount && parseFloat(customAmount) < share * months && (
                      <div style={{ fontSize: 10, color: '#B45309', marginTop: 4 }}>
                        ℹ️ आप ₹{parseFloat(customAmount).toLocaleString('en-IN')} दे रहे हैं — {months} माह की पूर्ण राशि ₹{(share * months).toLocaleString('en-IN')} से कम है, लेकिन स्वीकार किया जाएगा।
                      </div>
                    )}
                  </div>
                )}

                {/* विशेष समर्पण सहयोग toggle */}
                <button onClick={() => setVisheshMode(v => !v)} style={{
                  width: '100%', padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                  marginBottom: 16, textAlign: 'left', transition: 'all 0.2s',
                  background: visheshMode ? 'linear-gradient(135deg, #78350f, #92400e)' : '#fffbeb',
                  border: `2px solid ${visheshMode ? '#92400e' : '#FCD34D'}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: visheshMode ? '#FDE68A' : '#92400E' }}>
                      🌟 विशेष समर्पण सहयोगी बनें
                    </div>
                    <div style={{ fontSize: 11, color: visheshMode ? 'rgba(253,230,138,0.75)' : '#B45309', marginTop: 2 }}>
                      एकमुश्त ₹1,00,000 — विशेष सहयोगी का दर्जा
                    </div>
                  </div>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    background: visheshMode ? '#FDE68A' : '#fff',
                    border: `2px solid ${visheshMode ? '#FDE68A' : '#FCD34D'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, color: '#92400E', fontWeight: 900,
                  }}>{visheshMode ? '✓' : ''}</div>
                </button>

                {/* Summary box */}
                <div style={{
                  background: visheshMode ? 'linear-gradient(135deg,#78350f15,#92400e10)' : '#FFFBEB',
                  border: `1.5px solid ${visheshMode ? '#92400e' : '#FCD34D'}`,
                  borderRadius: 12, padding: '14px 16px', marginBottom: 20,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {visheshMode ? '🌟 विशेष समर्पण सहयोग' : restartMode ? '🌱 नई शुरुआत — आज से' : 'भुगतान सारांश'}
                    </div>
                    <div style={{ fontSize: 10, color: '#B45309' }}>🙏 जय गुरु महाराज री सा</div>
                  </div>

                  {/* Amount row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 28, fontWeight: 900, color: '#92400E', lineHeight: 1 }}>
                        ₹ {visheshMode ? '1,00,000' : totalAmt.toLocaleString('en-IN')}
                      </div>
                      <div style={{ fontSize: 11, color: '#B45309', marginTop: 4 }}>
                        {visheshMode
                          ? `एकमुश्त · ${visheshMonths} माह · ${fmtMon(startFrom)} → ${fmtMon(endAt)}`
                          : isOneTimePayer
                            ? 'एकमुश्त समर्पण'
                            : `₹${share.toLocaleString('en-IN')} × ${months} माह`}
                      </div>
                      {/* Currency estimate for NRI users */}
                      {(() => {
                        const amt = visheshMode ? 100000 : totalAmt;
                        const estimates = [
                          inrToForeign(amt, 'US'),
                          inrToForeign(amt, 'GB'),
                          inrToForeign(amt, 'AE'),
                        ].filter(Boolean);
                        return estimates.length ? (
                          <div style={{ fontSize: 10, color: '#9a3412', marginTop: 4, opacity: 0.8 }}>
                            💱 {estimates.join(' · ')}
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: '#92400E' }}>
                      <div style={{ fontWeight: 700 }}>अवधि</div>
                      <div>{fmtMon(startFrom)}</div>
                      <div>→ {fmtMon(endAt)}</div>
                    </div>
                  </div>

                </div>

                {/* Pay button */}
                <button
                  disabled={paying}
                  onClick={startRazorpayPayment}
                  style={{
                    width: '100%', height: 50, borderRadius: 12,
                    background: paying ? '#9ca3af' : visheshMode
                      ? 'linear-gradient(135deg, #78350f, #92400e)'
                      : `linear-gradient(135deg, ${M}, #b71c1c)`,
                    color: '#FDE68A', border: 'none', fontSize: 15, fontWeight: 800,
                    cursor: paying ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: paying ? 'none' : '0 4px 14px rgba(122,0,0,0.35)',
                    transition: 'all 0.2s',
                  }}>
                  {paying
                    ? <><Spin size="small" /> भुगतान हो रहा है…</>
                    : visheshMode
                      ? <>🌟 ₹1,00,000 — विशेष सहयोगी बनें</>
                      : <>🔐 ₹{totalAmt.toLocaleString('en-IN')} — Razorpay से भुगतान करें</>
                  }
                </button>

                {/* International card note */}
                <div style={{ textAlign: 'center', marginTop: 8, fontSize: 10, color: '#6b7280' }}>
                  🌍 {t('International Visa / Mastercard / Amex भी स्वीकार',
                        'International Visa / Mastercard / Amex accepted')}
                </div>

                {/* Fee transparency note */}
                <div style={{
                  background: '#F9FAFB', borderRadius: 8, padding: '8px 12px',
                  marginTop: 10, fontSize: 11, color: '#6B7280', lineHeight: 1.5,
                }}>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 2 }}>
                    💡 भुगतान की जानकारी
                  </div>
                  <div>• आपके द्वारा दी गई राशि <strong>₹{totalAmt.toLocaleString('en-IN')}</strong> आपकी receipt में दर्ज होगी</div>
                  <div>• Razorpay gateway शुल्क (~2%) समाज शिक्षा निधि वहन करेगा</div>
                  <div>• आपका पूरा समर्पण खाते में जमा होगा</div>
                </div>

                {/* Trust badges */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10 }}>
                  {['UPI', 'Cards', 'Net Banking', 'Wallet'].map(m => (
                    <div key={m} style={{ fontSize: 9, color: '#9CA3AF', fontWeight: 600, textAlign: 'center' }}>
                      ✓ {m}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Month-wise payment status */}
      {!isOneTimePayer && (() => {
        // Use allPayments (full unpaginated list) so grid shows all paid months correctly
        const paidMap = {};
        allPayments.forEach(p => {
          if (!p.start_month || !p.end_month) return;
          const s = new Date(p.start_month);
          const e = new Date(p.end_month);
          let cur = new Date(s.getFullYear(), s.getMonth(), 1);
          while (cur <= e) {
            const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`;
            paidMap[key] = p.payment_virify === 'verified' ? 'verified' : 'pending';
            cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
          }
        });

        const now = new Date();
        const allPaidKeys = Object.keys(paidMap).sort();

        // Determine start: joining/paid history OR fresh start from current month
        let startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        if (!freshStart) {
          if (joiningDate) {
            const jd = new Date(joiningDate);
            startDate = new Date(jd.getFullYear(), jd.getMonth(), 1);
          }
          if (allPaidKeys.length) {
            const [py, pm] = allPaidKeys[0].split('-').map(Number);
            const paidStart = new Date(py, pm - 1, 1);
            if (paidStart < startDate) startDate = paidStart;
          }
        }
        if (!joiningDate && !allPaidKeys.length) return null;

        const months = [];
        let cur = new Date(startDate);
        const limit = new Date(now.getFullYear(), now.getMonth(), 1);
        while (cur <= limit) {
          const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`;
          months.push({ key, date: new Date(cur), status: paidMap[key] || 'unpaid' });
          cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
        }

        const fmtM = d => d.toLocaleDateString('hi-IN', { month: 'short', year: '2-digit' });
        const unpaidCount = months.filter(m => m.status === 'unpaid').length;

        return (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: 16 }}>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>📅 माह-वार समर्पण स्थिति</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 8, fontSize: 10 }}>
                  <span style={{ color: '#15803d', fontWeight: 600 }}>● भुगतान</span>
                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>● pending</span>
                  <span style={{ color: '#ef4444', fontWeight: 600 }}>● शेष</span>
                </div>
                {/* Fresh-start toggle — only show if old months exist */}
                {joiningDate && (
                  <button
                    onClick={() => setFreshStart(f => !f)}
                    style={{
                      fontSize: 10, fontWeight: 700, borderRadius: 20,
                      padding: '3px 9px', cursor: 'pointer', border: 'none',
                      background: freshStart ? '#dcfce7' : '#fef3c7',
                      color: freshStart ? '#15803d' : '#92400e',
                    }}
                  >
                    {freshStart ? '← पूरा इतिहास' : 'नई शुरुआत →'}
                  </button>
                )}
              </div>
            </div>

            {/* Fresh-start info banner */}
            {freshStart && (
              <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '7px 10px', marginBottom: 8, fontSize: 11, color: '#15803d', fontWeight: 600 }}>
                🌱 नई शुरुआत — पुराने माह छोड़ कर आज से समर्पण करें
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {months.map(m => (
                m.status === 'unpaid' ? (
                  <button key={m.key}
                    onClick={() => openDonateModal(1, m.key)}
                    title="इस माह का समर्पण करें"
                    style={{
                      fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '4px 8px',
                      background: '#fef2f2', color: '#dc2626',
                      border: '1px solid #fca5a5',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}>
                    {fmtM(m.date)} ○
                  </button>
                ) : (
                  <span key={m.key} style={{
                    fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '4px 8px',
                    background: m.status === 'verified' ? '#f0fdf4' : '#fffbeb',
                    color:      m.status === 'verified' ? '#15803d' : '#b45309',
                    border: `1px solid ${m.status === 'verified' ? '#86efac' : '#fcd34d'}`,
                  }}>
                    {fmtM(m.date)}
                    {m.status === 'verified' && ' ✓'}
                    {m.status === 'pending'  && ' ⏳'}
                  </span>
                )
              ))}
            </div>
            {unpaidCount > 0 && (
              <div style={{ marginTop: 10, fontSize: 11, color: '#92400e', fontWeight: 600 }}>
                🙏 {unpaidCount} माह का समर्पण शेष है — जब भी संभव हो, समर्पित करें
              </div>
            )}
          </div>
        );
      })()}

      {/* Bank account dropdown filter */}
      {accounts.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>खाता फ़िल्टर करें</div>
          <select
            value={bankFilter || ''}
            onChange={e => selectBank(e.target.value || null)}
            style={{
              width: '100%', height: 42, borderRadius: 10,
              border: `1.5px solid ${bankFilter ? M : '#d1d5db'}`,
              padding: '0 12px', fontSize: 13, outline: 'none',
              background: '#fff', cursor: 'pointer',
              color: bankFilter ? M : '#374151', fontWeight: bankFilter ? 700 : 400,
            }}>
            <option value="">सभी खाते (All Accounts)</option>
            {accounts.filter(a => a.has_payments).map(a => (
              <option key={a.id} value={String(a.id)}>
                {a.account_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading
        ? <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        : payments.length === 0
          ? <Empty description="इस खाते में कोई payment नहीं मिला" />
          : payments.map(p => (
            <div key={p.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: M }}>₹ {fmtAmount(p.amount)}</div>
                  {p.receipt_no && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>Receipt #{p.receipt_no}</div>}
                </div>
                <Tag color={p.payment_virify === 'verified' ? 'green' : 'orange'} style={{ fontSize: 11 }}>
                  {p.payment_virify === 'verified' ? <><CheckCircleOutlined /> Verified</> : <><ClockCircleOutlined /> Pending</>}
                </Tag>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: p.remarks ? 8 : 12 }}>
                {[
                  { label: 'दिनांक',   value: fmtDate(p.transaction_date) },
                  { label: 'Mode',     value: p.pyament_mode_type },
                  { label: 'अवधि',    value: (p.start_month || p.end_month) ? `${fmtDate(p.start_month)} – ${fmtDate(p.end_month)}` : null },
                  { label: 'खाता',    value: p.bank_account },
                  { label: 'Category', value: p.special_occassion_category },
                  { label: 'UTR',      value: p.utr_number },
                ].filter(r => r.value).map(({ label, value }) => (
                  <div key={label} style={{ fontSize: 11 }}>
                    <span style={{ color: '#9ca3af' }}>{label}: </span>
                    <span style={{ color: '#374151', fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
              </div>
              {p.remarks && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, fontStyle: 'italic' }}>📝 {p.remarks}</div>}
              {p.comment && (p.comment.includes('Razorpay:') || p.comment.includes('Total fee:')) && (
                <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '4px 8px', marginBottom: 10 }}>
                  ℹ️ {p.comment.split('|').filter(s => s.includes('fee') || s.includes('Fee') || s.includes('Net') || s.includes('GST')).join(' |').trim()}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <Tooltip title={!p.receipt_no ? 'रसीद नं. तैयार हो रही है, कुछ देर प्रतीक्षा करें…' : ''}>
                  <Button size="small" icon={<EyeOutlined />}
                    onClick={() => p.receipt_no && setReceipt(p)}
                    disabled={!p.receipt_no}
                    style={{ borderRadius: 7, background: p.receipt_no ? M : '#d1d5db', borderColor: p.receipt_no ? M : '#d1d5db', color: p.receipt_no ? '#FFD700' : '#9ca3af', fontWeight: 700, flex: 1, cursor: p.receipt_no ? 'pointer' : 'not-allowed' }}>
                    Receipt
                  </Button>
                </Tooltip>
                <Button size="small" icon={<DownloadOutlined />}
                  onClick={() => downloadOrSharePoster(p.id, p.amount, p.transaction_date)}
                  style={{ borderRadius: 7, borderColor: '#1d4ed8', color: '#1d4ed8', fontWeight: 600, flex: 1 }}>
                  Poster / Share
                </Button>
              </div>
            </div>
          ))
      }

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <Button size="small" disabled={page === 1} onClick={() => fetchPayments(page - 1)} style={{ borderRadius: 7 }}>← पिछला</Button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Page {page} / {totalPages}</span>
          <Button size="small" disabled={page >= totalPages} onClick={() => fetchPayments(page + 1)} style={{ borderRadius: 7 }}>अगला →</Button>
        </div>
      )}

      {/* ── CHANGE PLAN MODAL ── */}
      <Modal
        open={changePlanOpen}
        onCancel={() => setChangePlanOpen(false)}
        footer={null} centered width={360} title={null} closable={false}
        styles={{ body: { padding: 0 } }}
      >
        <div>
          <div style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', padding: '16px 20px', borderRadius: '8px 8px 0 0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>✏️ मासिक राशि बदलें</div>
            <button onClick={() => setChangePlanOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none',
              borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', color: '#fff', fontSize: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
          <div style={{ padding: '20px' }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
              नई राशि चुनें — पुरानी subscription cancel होगी और नया UPI mandate setup करना होगा।
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 20 }}>
              {PLAN_AMOUNTS.map(amt => (
                <button key={amt} onClick={() => setNewPlanAmount(amt)} style={{
                  padding: '10px 6px', borderRadius: 10, fontWeight: 800, fontSize: 13,
                  cursor: 'pointer', transition: 'all 0.15s',
                  background: newPlanAmount === amt ? '#1d4ed8' : '#f9fafb',
                  color:      newPlanAmount === amt ? '#fff' : '#374151',
                  border:     newPlanAmount === amt ? '2px solid #1d4ed8' : '1.5px solid #e5e7eb',
                }}>
                  ₹{amt.toLocaleString('en-IN')}
                  <div style={{ fontSize: 9, fontWeight: 500, marginTop: 2,
                    color: newPlanAmount === amt ? 'rgba(255,255,255,0.75)' : '#9ca3af' }}>
                    /माह
                  </div>
                </button>
              ))}
            </div>
            {newPlanAmount && (
              <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8,
                padding: '10px 12px', marginBottom: 14, fontSize: 11, color: '#1e40af' }}>
                ℹ️ आपकी राशि <strong>₹{newPlanAmount.toLocaleString('en-IN')}/माह</strong> हो जाएगी।
                पुरानी subscription तुरंत cancel होगी — नया UPI Autopay approve करना होगा।
              </div>
            )}
            <button
              disabled={!newPlanAmount || subLoading}
              onClick={confirmChangePlan}
              style={{
                width: '100%', height: 44, borderRadius: 10, border: 'none',
                background: newPlanAmount ? '#1d4ed8' : '#e5e7eb',
                color: newPlanAmount ? '#fff' : '#9ca3af',
                fontSize: 14, fontWeight: 800,
                cursor: newPlanAmount ? 'pointer' : 'default',
              }}>
              {subLoading ? 'Processing…' : newPlanAmount ? `₹${newPlanAmount.toLocaleString('en-IN')}/माह करें →` : 'राशि चुनें'}
            </button>
          </div>
        </div>
      </Modal>

      <ReceiptModal payment={receipt} open={!!receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

